import { Component, OnInit, Inject } from '@angular/core';
import {
	MatButtonToggleChange,
	MatButtonToggle,
} from '@angular/material/button-toggle';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	FormGroup,
	FormBuilder,
	FormControl,
	Validators,
} from '@angular/forms';
import { animateError } from 'src/shared/animations';
import {
	JobsClient,
	BulkAddShiftsRequest,
	BulkDeleteShiftsRequest,
	JobShift,
	Job,
	ServiceType
} from 'src/shared/api';
import { timeMask } from 'src/shared/constants/constants';
import * as moment from 'moment';

export interface ShiftDialogData {
	add: boolean;
	delete: boolean;
	addSingle: boolean;
	job: Job;
	showServiceType?: boolean;
	initialStartDate?: moment.Moment;
	initialEndDate?: moment.Moment;
}

@Component({
	templateUrl: './add-edit-shift-dialog.component.html',
	styleUrls: ['./add-edit-shift-dialog.component.scss'],
	animations: [animateError],
})
export class AddEditShiftDialogComponent implements OnInit {
	shiftsForm: FormGroup;
	deleteConflicts = false;
	readonly timeMask = timeMask;

	constructor(
		private formBuilder: FormBuilder,
		private jobsClient: JobsClient,
		private shiftsDialogRef: MatDialogRef<AddEditShiftDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ShiftDialogData
	) { }

	ngOnInit() {
		const initialStartDate = this.data.initialStartDate || moment.utc();
		const initialEndDate = this.data.initialEndDate || this.data.job.end;
		if (this.data.add) {
			this.shiftsForm = this.formBuilder.group({
				startDate: new FormControl(
					initialStartDate,
					Validators.required
				),
				endDate: new FormControl(initialEndDate, Validators.required),
				startTime: new FormControl(
					'',
					Validators.pattern(
						'^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$'
					)
				),
				countPerDay: new FormControl('1', Validators.required),
				shiftsWeek: this.formBuilder.group({
					sunday: new FormControl(false, Validators.requiredTrue),
					monday: new FormControl(false, Validators.requiredTrue),
					tuesday: new FormControl(false, Validators.requiredTrue),
					wednesday: new FormControl(false, Validators.requiredTrue),
					thursday: new FormControl(false, Validators.requiredTrue),
					friday: new FormControl(false, Validators.requiredTrue),
					saturday: new FormControl(false, Validators.requiredTrue),
				}),
				serviceType: new FormControl(
					'',
					this.data.showServiceType
						? Validators.required
						: Validators.nullValidator
				),
			});
		} else if (this.data.addSingle) {
			this.shiftsForm = this.formBuilder.group({
				startDate: new FormControl(
					initialStartDate,
					Validators.required
				),
				startTime: new FormControl(
					'',
					Validators.pattern(
						'^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$'
					)
				),
				serviceType: new FormControl(
					'',
					this.data.showServiceType
						? Validators.required
						: Validators.nullValidator
				),
				countPerDay: new FormControl('1', Validators.required),
			});
		} else if (this.data.delete) {
			this.shiftsForm = this.formBuilder.group({
				startDate: new FormControl(
					initialStartDate,
					Validators.required
				),
				endDate: new FormControl(initialEndDate, Validators.required),
			});
		}
	}

	get shiftsWeek() {
		return this.shiftsForm.get('shiftsWeek') as FormGroup;
	}

	showToggleButtonError(): boolean {
		return (
			this.shiftsWeek.controls['sunday'].hasError('required') &&
			this.shiftsWeek.dirty
		);
	}

	isDaySelected(event: MatButtonToggleChange): void {
		const buttonGroup: MatButtonToggle[] = event.source.buttonToggleGroup
			.selected as MatButtonToggle[];
		this.shiftsWeek.get(event.source.id).patchValue(event.source.checked);

		for (const control in this.shiftsWeek.controls) {
			if (control) {
				if (buttonGroup.length === 0) {
					this.shiftsWeek.controls[control].setValidators(
						Validators.requiredTrue
					);
					this.shiftsWeek.markAsDirty();
				} else {
					this.shiftsWeek.controls[control].clearValidators();
				}

				this.shiftsWeek.controls[control].updateValueAndValidity();
			}
		}
	}

	addShift(): void {
		const shift: JobShift = {
			day: this.shiftsForm.value.startDate,
			startTime: this.shiftsForm.value.startTime,
			serviceType: this.getServiceType(),
			job: this.data.job,
		};

		this.shiftsDialogRef.close(shift);
	}

	addShifts(): void {
		const bulkAddShifts: BulkAddShiftsRequest = {
			jobId: this.data.job.id,
			startDate: moment
				.utc(this.shiftsForm.value.startDate)
				.startOf('d')
				.toJSON(),
			endDate: moment
				.utc(this.shiftsForm.value.endDate)
				.startOf('d')
				.toJSON(),
			startTime: this.shiftsForm.value.startTime,
			countPerDay: this.shiftsForm.value.countPerDay,
			sunday: this.shiftsWeek.value.sunday,
			monday: this.shiftsWeek.value.monday,
			tuesday: this.shiftsWeek.value.tuesday,
			wednesday: this.shiftsWeek.value.wednesday,
			thursday: this.shiftsWeek.value.thursday,
			friday: this.shiftsWeek.value.friday,
			saturday: this.shiftsWeek.value.saturday,
			serviceTypeId: this.getServiceType()?.id,
		};

		this.shiftsDialogRef.close(bulkAddShifts);
	}

	deleteShifts(): void {
		const bulkDeleteShiftRequest: BulkDeleteShiftsRequest = {
			jobId: this.data.job.id,
			startDate: moment
				.utc(this.shiftsForm.value.startDate)
				.startOf('d')
				.toJSON(),
			endDate: moment
				.utc(this.shiftsForm.value.endDate)
				.startOf('d')
				.toJSON(),
		};

		this.jobsClient
			.countShiftsWithRwics(bulkDeleteShiftRequest)
			.subscribe((data: number) => {
				if (data > 0) {
					this.deleteConflicts = true;
				}
			});

		if (!this.deleteConflicts) {
			this.shiftsDialogRef.close(bulkDeleteShiftRequest);
		}
	}

	deleteShiftsAnyway(): void {
		const bulkDeleteShiftRequest: BulkDeleteShiftsRequest = {
			jobId: this.data.job.id,
			startDate: this.shiftsWeek.value.startDate,
			endDate: this.shiftsWeek.value.endDate,
		};

		this.shiftsDialogRef.close(bulkDeleteShiftRequest);
		this.deleteConflicts = false;
	}

	cancel(): void {
		this.shiftsDialogRef.close();
	}

	private getServiceType(): ServiceType {
		const { offeredServices, contractedRates } = this.data.job ?? { offeredServices: [], contractedRates: [] };

		if (offeredServices.length > 1 || contractedRates.length > 1) {
			return this.shiftsForm.value.serviceType as ServiceType;
		}

		return offeredServices[0]?.type ?? contractedRates[0]?.serviceType ?? null;
	}
}
